import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Checkbox, MessageBarBody, MessageBarTitle, ProgressBar, Spinner } from '@fluentui/react-components';
import { Add20Regular, ArrowDownload20Regular } from '@fluentui/react-icons';

// functions
import * as Connect from './PractitionerAccountStatement.Connect';
import {
    BalanceByCurrency,
    buildBalance,
    buildSettlementRows,
    canPayRow,
    currenciesOf,
    filterPayments,
    isOtherReceiver,
    PERIOD_KEYS,
    PeriodKey,
    paymentSettlementIds,
    pendingDebtsOf,
    patientStatusKey,
    periodRange,
    protocolLabel,
    SettlementRow,
    settlementLabel,
    settlementStatusKey,
    settlementStatusTone,
    subtotalsByCurrency,
    visitsByMonth
} from './PractitionerAccountStatement.Functions';
import { accountStatementFileName, accountStatementSheets } from './PractitionerAccountStatement.Excel';
import { downloadWorkbook } from '../../Contracts/statistics/StatisticsExcel.Functions';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import { useTranslation } from 'react-i18next';

// components
import { BlockSkeleton, ErrorBanner, HelpButton, UnavailableBanner, useStatementText } from './PractitionerAccountStatement.Bits';
import { VisitsByMonthChart } from './PractitionerAccountStatement.Chart';
import { StatusPill } from '../../../theme/components/StatusPill';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideSectionTitle } from '../../../theme/components/GuideSectionTitle';
import { GuideCountChip, GuideKpiCard, GuideKpiGrid } from '../../../theme/components/GuideKpiCard';
import { GuideSegmented } from '../../../theme/components/GuideSegmented';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideLinkButton } from '../../../theme/components/GuideButtons';
import { GuideRowActions, GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName, useGuideSort } from '../../../theme/components/GuideTable';
import { GuidePagination, useGuidePagination } from '../../../theme/components/GuidePagination';
import PaymentFormComponent from '../../CashFlow/Payments/Payment.Form/Payment.Form.Component';
import { PractitionerInvoicesSection } from './Invoices/PractitionerInvoices.Section';
import { PaymentInvoicesDrawer } from '../../CashFlow/Payments/PaymentInvoices/PaymentInvoices.Drawer';
import { PaymentCounters } from '../../CashFlow/Payments/PaymentInvoices/PaymentCounters';
import AzureFileUploadDrawer from '../../Commons/AzureFileUploadDrawer/AzureFileUploadDrawer.Component';
import { buildPaymentReceiptTarget, PAYMENT_RECEIPT_ALLOWED_FILE_TYPES } from '../../../functions/AzureFileUpload.Functions';
import { FormType as PaymentFormType } from '../../CashFlow/Payments/Payment.Form/Payment.Form.Function';

// models
import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { CreditorPayment, SectionState } from '../../../models/Entities/PractitionerAccountStatement/PractitionerAccountStatement';

// styles
import './PractitionerAccountStatement.Styles.css';

const fmt = (amount: number, currency: string) => formatCurrencyAmountWithIssue(amount, currency);
const fmtDate = (value: Date | string | null | undefined): string => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const toTime = (value: Date | string | null | undefined): number | undefined => {
    if (!value) return undefined;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? undefined : time;
};

const ALL_CURRENCIES = '__all';
/** La paginación de pagos aparece cuando hay más filas que la página más chica. */
const PAYMENTS_PAGINATION_FROM = 10;

const isLoadingFirst = (section: SectionState<unknown>) => section.status === 'loading' && section.data === undefined;
const isIdle = (section: SectionState<unknown>) => section.status === 'idle';

/**
 * Estado de cuenta del profesional, pensado para el personal administrativo:
 * cuánto le debemos (por moneda, nunca mezcladas), cuánto le pagamos y a quién,
 * el detalle por liquidación con el botón para pagar, su actividad y el Excel.
 * Maquetado con los componentes de la guía (src/theme/components).
 */
export const PractitionerAccountStatementComponent = (props: Connect.Props) => {
    const tr = useStatementText();
    const [t] = useTranslation();
    const practitionerId = props.PractitionerId;
    const name = props.PractitionerName || props.NameOf(practitionerId);

    // ── Filtros ──
    const [periodKey, setPeriodKey] = useState<PeriodKey>('year');
    const [currency, setCurrency] = useState<string | undefined>(undefined);
    const period = useMemo(() => periodRange(periodKey), [periodKey]);

    // ── Pago ──
    const [paymentOpen, setPaymentOpen] = useState<boolean>(false);
    const [prefill, setPrefill] = useState<Debt[] | undefined>(undefined);
    const [gathering, setGathering] = useState<string | undefined>(undefined);
    const [payError, setPayError] = useState<string | undefined>(undefined);
    const [selected, setSelected] = useState<string[]>([]);

    // ── Facturas y comprobantes de un pago ──
    const [invoicesPayment, setInvoicesPayment] = useState<CreditorPayment | undefined>(undefined);
    const [receiptsPayment, setReceiptsPayment] = useState<CreditorPayment | undefined>(undefined);
    const [invoicesChanged, setInvoicesChanged] = useState<boolean>(false);

    // ── Otros ──
    const [expandedPayment, setExpandedPayment] = useState<number | undefined>(undefined);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [excelError, setExcelError] = useState<boolean>(false);

    const loadMoney = () => props.LoadMoneySections(practitionerId, props.OwnerId);
    const loadPatients = () => props.LoadPatientsSummary(practitionerId);
    const loadVisits = () => props.LoadVisitsSummary(practitionerId, period);

    useEffect(() => {
        if (!practitionerId) return;
        loadMoney();
        loadPatients();
        props.GetAllPersons();
        props.GetAllPractitioners(1, 1000, undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practitionerId, props.OwnerId]);

    useEffect(() => {
        if (practitionerId) loadVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practitionerId, periodKey]);

    // ── Datos ──
    const { debts: debtsState, settlementSummary, payments: paymentsState, settlements: settlementsState, patients, visits } = props.Statement;
    const debts = debtsState.data ?? [];
    const summaries = settlementSummary.data ?? [];
    const payments = paymentsState.data ?? [];
    const settlements = settlementsState.data ?? [];

    const currencies = useMemo(() => currenciesOf(summaries, payments, debts), [summaries, payments, debts]);
    const activeCurrency = currency && currencies.includes(currency) ? currency : undefined;

    const balance = useMemo(
        () => buildBalance({ summaries, payments, debts, period, practitionerId, currency: activeCurrency }),
        [summaries, payments, debts, period, practitionerId, activeCurrency]
    );
    const rows = useMemo(() => buildSettlementRows(summaries, settlements, activeCurrency), [summaries, settlements, activeCurrency]);
    const subtotals = useMemo(() => subtotalsByCurrency(rows), [rows]);
    const visiblePayments = useMemo(() => filterPayments(payments, period, activeCurrency), [payments, period, activeCurrency]);
    const pendingDebts = useMemo(() => pendingDebtsOf(debts), [debts]);

    const periodLabel = tr(`filters.periods.${periodKey}`);
    const statusLabel = (status: number | undefined) => t(settlementStatusKey(status));
    const patientStatusLabel = (status: number | null, statusName: string): string => {
        const key = patientStatusKey(status);
        return key ? tr(key) : (statusName?.trim() || tr('patientStatus.unknown'));
    };
    const settlementNameOf = (id: number | null | undefined) =>
        id === null || id === undefined ? tr('settlements.noSettlement') : settlementLabel(id, settlements);

    // ── Orden y paginación de las grillas (encabezados ordenables de la guía) ──
    const settlementSort = useGuideSort<SettlementRow>(rows, {
        name: (row) => (row.settlementId === null ? tr('settlements.noSettlement') : row.name),
        period: (row) => toTime(row.periodFrom),
        status: (row) => (row.status === undefined ? undefined : statusLabel(row.status)),
        original: (row) => row.original,
        paid: (row) => row.paid,
        pending: (row) => row.pending,
        progress: (row) => row.progress
    });
    const paymentSort = useGuideSort<CreditorPayment>(visiblePayments, {
        date: (payment) => toTime(payment.paymentDate),
        amount: (payment) => payment.amount,
        imputed: (payment) => payment.totalImputedToCreditor,
        receiver: (payment) => props.NameOf(payment.receiver)
    });
    const paymentPages = useGuidePagination(paymentSort.rows, 25);

    // ── Acciones de pago ──
    const openPayment = (debtsToPrefill?: Debt[]) => {
        setPrefill(debtsToPrefill && debtsToPrefill.length > 0 ? debtsToPrefill : undefined);
        setPaymentOpen(true);
    };

    const paySettlements = async (settlementIds: number[], key: string) => {
        setPayError(undefined);
        setGathering(key);
        const local = pendingDebts.filter((debt) => debt.settlementId !== undefined && debt.settlementId !== null && settlementIds.includes(debt.settlementId));
        try {
            const fetched: Debt[] = await props.GetSettlementDebtsForPayment(practitionerId, settlementIds);
            openPayment(fetched.length > 0 ? fetched : local);
        } catch {
            if (local.length > 0) openPayment(local);
            else setPayError(tr('settlements.payError'));
        } finally {
            setGathering(undefined);
        }
    };

    const closePayment = () => {
        setPaymentOpen(false);
        setPrefill(undefined);
        setSelected([]);
        // Los saldos cambiaron: se vuelven a leer (y las facturas, que el pago pudo cubrir).
        loadMoney();
        props.LoadCreditorInvoices?.(practitionerId);
    };

    const closeInvoicesDrawer = () => {
        setInvoicesPayment(undefined);
        // Los contadores de facturas del pago vienen del servidor: se releen si algo cambió.
        if (invoicesChanged) loadMoney();
        setInvoicesChanged(false);
    };

    const closeReceiptsDrawer = () => {
        setReceiptsPayment(undefined);
        loadMoney();
    };

    const selectedRows = rows.filter((row) => selected.includes(row.key));
    const selectedIds = Array.from(new Set(selectedRows.map((row) => row.settlementId).filter((id): id is number => id !== null)));
    const selectedCurrencies = Array.from(new Set(selectedRows.map((row) => row.currency)));
    const toggleRow = (key: string) => setSelected((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));
    const payableKeys = rows.filter(canPayRow).map((row) => row.key);
    const allPayableSelected = payableKeys.length > 0 && payableKeys.every((key) => selected.includes(key));
    const somePayableSelected = payableKeys.some((key) => selected.includes(key));
    const toggleAll = () => setSelected(allPayableSelected ? [] : payableKeys);

    // ── Excel ──
    const download = async () => {
        setDownloading(true);
        setExcelError(false);
        try {
            const sheets = accountStatementSheets({
                practitionerName: name,
                practitionerId,
                periodLabel,
                balance,
                settlementRows: rows,
                payments: visiblePayments,
                pendingDebts: pendingDebtsOf(debts, activeCurrency),
                settlements,
                patients: patients.data,
                visits: visits.data,
                invoices: props.Invoices?.status === 'unavailable' ? undefined : props.Invoices?.data,
                nameOf: props.NameOf,
                statusLabel,
                patientStatusLabel
            });
            await downloadWorkbook(accountStatementFileName(name), sheets, {
                title: tr('excel.title'),
                scope: `${name} · ${periodLabel}${activeCurrency ? ` · ${activeCurrency}` : ''}`
            });
        } catch {
            setExcelError(true);
        } finally {
            setDownloading(false);
        }
    };

    // ── 1. Saldo ──
    const renderBalanceCurrency = (row: BalanceByCurrency) => (
        <div key={row.currency} className="pas-balance__group" data-testid={`pas-balance-${row.currency}`}>
            <p className="ichtys-eyebrow">{row.currency}</p>
            <GuideKpiGrid>
                <GuideKpiCard
                    label={tr('balance.owe')}
                    help={<HelpButton title={tr('balance.owe')} text={tr('balance.oweHelp')} />}
                    value={fmt(row.pending, row.currency)}
                    valueTestId={`pas-owe-${row.currency}`}
                    tone={row.pending > 0 ? 'warning' : 'neutral'}
                    detail={tr('balance.oweDetail', { count: row.pendingDebts })}
                />
                <GuideKpiCard
                    label={tr('balance.paid')}
                    help={<HelpButton title={tr('balance.paid')} text={tr('balance.paidHelp')} />}
                    value={fmt(row.paidInPeriod, row.currency)}
                    detail={
                        <>
                            {tr('balance.paidDetail', { count: row.paymentsInPeriod, period: periodLabel.toLowerCase() })}
                            {row.paidToOthersInPeriod > 0 && <> · {tr('balance.paidToOthers', { amount: fmt(row.paidToOthersInPeriod, row.currency) })}</>}
                        </>
                    }
                />
                <GuideKpiCard
                    label={tr('balance.settlements')}
                    help={<HelpButton title={tr('balance.settlements')} text={tr('balance.settlementsHelp')} />}
                    value={row.pendingSettlements}
                    detail={tr('balance.settlementsDetail', { amount: fmt(row.original, row.currency), paid: fmt(row.paid, row.currency) })}
                />
            </GuideKpiGrid>
        </div>
    );

    const renderBalance = () => {
        if (settlementSummary.status === 'failed' && summaries.length === 0) {
            return <ErrorBanner title={tr('balance.errorTitle')} error={settlementSummary.error} onRetry={loadMoney} />;
        }
        if (isIdle(settlementSummary) || isLoadingFirst(settlementSummary) || isLoadingFirst(paymentsState)) {
            return <BlockSkeleton rows={2} testId="pas-balance-loading" />;
        }
        if (balance.length === 0) {
            return <GuideEmptyState title={tr('balance.emptyTitle')} text={tr('balance.emptyText')} />;
        }
        return (
            <>
                <div className="pas-balance">{balance.map(renderBalanceCurrency)}</div>
                {balance.length > 1 && <p className="ichtys-secondary pas-caption">{tr('balance.neverMixed')}</p>}
            </>
        );
    };

    // ── 3. Por liquidación ──
    const renderSettlementRow = (row: SettlementRow) => {
        const payable = canPayRow(row);
        const isSelected = selected.includes(row.key);
        const pending = row.pending > 0;
        return (
            <tr key={row.key} className={guideRowClassName({ selected: isSelected, pending })} data-testid={`pas-settlement-row-${row.key}`}>
                <td className={cell('check')}>
                    <Checkbox
                        checked={isSelected}
                        disabled={!payable}
                        aria-label={tr('settlements.selectRow', { name: row.name })}
                        onChange={() => toggleRow(row.key)}
                    />
                </td>
                <td>
                    {row.settlementId !== null && <span className="pas-cell-id ichtys-num">LIQ-{row.settlementId}</span>}
                    <span className="pas-strong">{row.settlementId === null ? tr('settlements.noSettlement') : row.name}</span>
                </td>
                <td className="pas-secondary-cell pas-nowrap ichtys-num">{fmtDate(row.periodFrom)} – {fmtDate(row.periodTo)}</td>
                <td>{row.settlementId !== null && row.status !== undefined
                    ? (
                        <StatusPill tone={settlementStatusTone(row.status)} symbol={settlementStatusTone(row.status) === 'success' ? '✓' : undefined}>
                            {statusLabel(row.status)}
                        </StatusPill>
                    )
                    : <span className="pas-secondary-cell">—</span>}</td>
                <td className={cell('end', 'num')}>{fmt(row.original, row.currency)}</td>
                <td className={cell('end', 'num')}>{fmt(row.paid, row.currency)}</td>
                <td className={cell('end', pending ? 'amount' : 'num', pending && 'warning')}>{fmt(row.pending, row.currency)}</td>
                <td className="pas-progress">
                    <ProgressBar value={row.progress / 100} thickness="large" color={pending ? 'brand' : 'success'} aria-label={tr('settlements.progressAria', { value: row.progress })} />
                    <span className="pas-progress__text ichtys-num">{row.progress}% · {tr(`settlements.paymentState.${row.paymentState}`)}</span>
                </td>
                <td className={cell('actions')}>
                    {payable && (
                        <GuideRowActions
                            label={tr('settlements.rowActions', { name: row.name })}
                            busy={gathering === row.key}
                            disabled={!!gathering}
                            actions={[{
                                key: 'pay',
                                label: tr('actions.pay'),
                                onClick: () => { if (row.settlementId !== null) paySettlements([row.settlementId], row.key); }
                            }]}
                        />
                    )}
                </td>
            </tr>
        );
    };

    const renderSettlements = () => {
        if ((settlementSummary.status === 'failed' && summaries.length === 0) || (settlementsState.status === 'failed' && settlements.length === 0 && summaries.length === 0)) {
            return <ErrorBanner title={tr('settlements.errorTitle')} error={settlementSummary.error ?? settlementsState.error} onRetry={loadMoney} />;
        }
        if (isIdle(settlementSummary) || isLoadingFirst(settlementSummary)) return <BlockSkeleton rows={4} testId="pas-settlements-loading" />;
        if (rows.length === 0) return <GuideEmptyState title={tr('settlements.emptyTitle')} text={tr('settlements.emptyText')} />;

        const sort = settlementSort.sortProps;
        return (
            <>
                {settlementSummary.source === 'fallback' && <p className="ichtys-secondary pas-caption">{tr('settlements.fallback')}</p>}
                {settlementsState.status === 'failed' && (
                    <ErrorBanner title={tr('settlements.namesErrorTitle')} error={settlementsState.error} onRetry={loadMoney} />
                )}
                {selected.length > 0 && (
                    <div className="pas-selection" role="region" aria-label={tr('settlements.selectionAria')}>
                        <span className="pas-strong">{tr('settlements.selected', { count: selected.length })}</span>
                        <span className="ichtys-num pas-strong">
                            {subtotalsByCurrency(selectedRows).map((total) => fmt(total.pending, total.currency)).join(' · ')}
                        </span>
                        {selectedCurrencies.length > 1 && <span className="ichtys-secondary">{tr('settlements.mixedCurrencies')}</span>}
                        <span className="pas-spacer" />
                        <GuideLinkButton onClick={() => setSelected([])}>{tr('actions.clearSelection')}</GuideLinkButton>
                        <Button appearance="primary" disabled={!!gathering || selectedIds.length === 0} onClick={() => paySettlements(selectedIds, 'selection')}>
                            {gathering === 'selection' ? <><Spinner size="extra-tiny" /> {tr('actions.gathering')}</> : tr('actions.paySelected')}
                        </Button>
                    </div>
                )}
                <GuideTable ariaLabel={tr('settlements.title')}>
                    <thead>
                        <tr>
                            <th className={cell('check')} scope="col">
                                <Checkbox
                                    checked={allPayableSelected ? true : somePayableSelected ? 'mixed' : false}
                                    disabled={payableKeys.length === 0}
                                    aria-label={tr('settlements.selectAll')}
                                    onChange={toggleAll}
                                />
                            </th>
                            <GuideTh sort={sort('name')}>{tr('settlements.columns.name')}</GuideTh>
                            <GuideTh sort={sort('period')}>{tr('settlements.columns.period')}</GuideTh>
                            <GuideTh sort={sort('status')}>{tr('settlements.columns.status')}</GuideTh>
                            <GuideTh align="end" sort={sort('original')}>{tr('settlements.columns.original')}</GuideTh>
                            <GuideTh align="end" sort={sort('paid')}>{tr('settlements.columns.paid')}</GuideTh>
                            <GuideTh align="end" sort={sort('pending')}>{tr('settlements.columns.pending')}</GuideTh>
                            <GuideTh sort={sort('progress')}>{tr('settlements.columns.progress')}</GuideTh>
                            <th className={cell('actions')} scope="col" />
                        </tr>
                    </thead>
                    {subtotals.map((total) => (
                        <tbody key={total.currency}>
                            {settlementSort.rows.filter((row) => row.currency === total.currency).map(renderSettlementRow)}
                            <tr className={guideRowClassName({ total: true })} data-testid={`pas-subtotal-${total.currency}`}>
                                <td />
                                <td colSpan={3}>{tr('settlements.subtotal', { currency: total.currency, count: total.count })}</td>
                                <td className={cell('end', 'num')}>{fmt(total.original, total.currency)}</td>
                                <td className={cell('end', 'num')}>{fmt(total.paid, total.currency)}</td>
                                <td className={cell('end', 'amount', total.pending > 0 && 'warning')}>{fmt(total.pending, total.currency)}</td>
                                <td colSpan={2} />
                            </tr>
                        </tbody>
                    ))}
                </GuideTable>
            </>
        );
    };

    // ── 4. Pagos ──
    const renderPaymentDetail = (payment: CreditorPayment) => (
        <tr className={guideRowClassName({ detail: true })} key={`${payment.paymentId}-detail`}>
            <td colSpan={8}>
                <div className="pas-detail__title">{tr('payments.detail.title', { id: payment.paymentId })}</div>
                {(payment.imputations ?? []).length === 0
                    ? <span className="ichtys-secondary">{tr('payments.detail.none')}</span>
                    : (
                        <ul className="pas-detail__list">
                            {payment.imputations.map((item) => (
                                <li key={item.debtId}>
                                    {tr('payments.detail.debt', { id: item.debtId })} · {settlementNameOf(item.settlementId)} ·{' '}
                                    <span className="ichtys-num">{fmt(item.imputedAmountInDebtCurrency, item.debtCurrencyCode)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
            </td>
        </tr>
    );

    const renderPayments = () => {
        if (paymentsState.status === 'failed' && payments.length === 0) {
            return <ErrorBanner title={tr('payments.errorTitle')} error={paymentsState.error} onRetry={loadMoney} />;
        }
        if (isIdle(paymentsState) || isLoadingFirst(paymentsState)) return <BlockSkeleton rows={3} testId="pas-payments-loading" />;

        const sort = paymentSort.sortProps;
        return (
            <>
                {paymentsState.source === 'fallback' && (
                    <GuideMessageBar intent="warning" layout="multiline" data-testid="pas-payments-fallback">
                        <MessageBarBody>
                            <MessageBarTitle>{tr('payments.fallbackTitle')}</MessageBarTitle>
                            {tr('payments.fallbackText')}
                        </MessageBarBody>
                    </GuideMessageBar>
                )}
                {visiblePayments.length === 0
                    ? (
                        <GuideEmptyState
                            title={tr('payments.emptyTitle')}
                            text={tr('payments.emptyText')}
                            action={<Button appearance="primary" icon={<Add20Regular />} onClick={() => openPayment()}>{tr('actions.registerPayment')}</Button>}
                        />
                    )
                    : (
                        <GuideTable
                            ariaLabel={tr('payments.title')}
                            footer={visiblePayments.length > PAYMENTS_PAGINATION_FROM ? <GuidePagination {...paymentPages.paginationProps} /> : undefined}
                        >
                            <thead>
                                <tr>
                                    <GuideTh sort={sort('date')}>{tr('payments.columns.date')}</GuideTh>
                                    <GuideTh align="end" sort={sort('amount')}>{tr('payments.columns.amount')}</GuideTh>
                                    <GuideTh align="end" sort={sort('imputed')}>{tr('payments.columns.imputed')}</GuideTh>
                                    <GuideTh sort={sort('receiver')}>{tr('payments.columns.receiver')}</GuideTh>
                                    <GuideTh>{tr('payments.columns.settlements')}</GuideTh>
                                    <GuideTh>{tr('payments.columns.comments')}</GuideTh>
                                    <GuideTh>{t('creditorInvoices.counters.invoicesAction')} · {t('creditorInvoices.counters.receiptsAction')}</GuideTh>
                                    <th className={cell('end')} scope="col" />
                                </tr>
                            </thead>
                            <tbody>
                                {paymentPages.pageRows.map((payment) => {
                                    const other = isOtherReceiver(payment, practitionerId);
                                    const expanded = expandedPayment === payment.paymentId;
                                    const ids = paymentSettlementIds(payment);
                                    const code = (payment.currencyCode || '').toUpperCase();
                                    return (
                                        <React.Fragment key={payment.paymentId}>
                                            <tr className={guideRowClassName()} data-testid={`pas-payment-row-${payment.paymentId}`}>
                                                <td className="pas-nowrap ichtys-num">{fmtDate(payment.paymentDate)}</td>
                                                <td className={cell('end', 'amount')}>{fmt(payment.amount, code)}</td>
                                                <td className={cell('end', 'num')}>{fmt(payment.totalImputedToCreditor, code)}</td>
                                                <td>
                                                    <span className="pas-strong">{props.NameOf(payment.receiver)}</span>
                                                    {other && <StatusPill tone="warning" className="pas-pill">{tr('payments.otherReceiver')}</StatusPill>}
                                                </td>
                                                <td>{ids.length > 0 ? ids.map((id) => settlementNameOf(id)).join(', ') : <span className="pas-secondary-cell">—</span>}</td>
                                                <td className="pas-secondary-cell">{payment.comments || '—'}</td>
                                                <td>
                                                    <PaymentCounters
                                                        paymentId={payment.paymentId}
                                                        invoiceCount={props.InvoiceCountOf?.(payment.paymentId) ?? payment.invoiceCount}
                                                        receiptCount={props.ReceiptCountOf?.(payment.paymentId) ?? payment.receiptCount}
                                                        onInvoices={() => setInvoicesPayment(payment)}
                                                        onReceipts={() => setReceiptsPayment(payment)}
                                                    />
                                                </td>
                                                <td className={cell('end')}>
                                                    <GuideLinkButton size="small" aria-expanded={expanded}
                                                        onClick={() => setExpandedPayment(expanded ? undefined : payment.paymentId)}>
                                                        {expanded ? tr('actions.hideDetail') : tr('actions.viewDetail')}
                                                    </GuideLinkButton>
                                                </td>
                                            </tr>
                                            {expanded && renderPaymentDetail(payment)}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </GuideTable>
                    )}
            </>
        );
    };

    // ── 5. Actividad ──
    const renderPatients = () => {
        if (patients.status === 'unavailable') return <UnavailableBanner what={tr('activity.patientsWhat')} />;
        if (patients.status === 'failed') return <ErrorBanner title={tr('activity.errorPatients')} error={patients.error} onRetry={loadPatients} />;
        if (isIdle(patients) || isLoadingFirst(patients)) return <BlockSkeleton rows={2} testId="pas-patients-loading" />;
        const data = patients.data!;
        const hasDetail = data.activePatients !== undefined || data.source === 'Consumptions';
        return (
            <GuideKpiCard
                data-testid="pas-patients"
                label={tr('activity.patients')}
                help={<HelpButton title={tr('activity.patients')} text={tr('activity.patientsHelp')} />}
                value={data.totalPatients}
                detail={hasDetail ? (
                    <>
                        {data.activePatients !== undefined && <span className="pas-detail-line">{tr('activity.patientsActive', { count: data.activePatients })}</span>}
                        {data.source === 'Consumptions' && <span className="pas-detail-line" data-testid="pas-patients-source">{tr('activity.fromConsumptions')}</span>}
                    </>
                ) : undefined}
            >
                {data.byStatus.length > 0 && data.byStatus.map((item, index) => (
                    <GuideCountChip key={`${item.status ?? 'none'}-${index}`} label={patientStatusLabel(item.status, item.statusName)} count={item.count} />
                ))}
            </GuideKpiCard>
        );
    };

    const renderVisits = () => {
        if (visits.status === 'unavailable') return <UnavailableBanner what={tr('activity.visitsWhat')} />;
        if (visits.status === 'failed') return <ErrorBanner title={tr('activity.errorVisits')} error={visits.error} onRetry={loadVisits} />;
        if (isIdle(visits) || isLoadingFirst(visits)) return <BlockSkeleton rows={2} testId="pas-visits-loading" />;
        const data = visits.data!;
        const points = visitsByMonth(data);
        return (
            <>
                <GuideKpiCard
                    data-testid="pas-visits"
                    label={tr('activity.visits')}
                    help={<HelpButton title={tr('activity.visits')} text={tr('activity.visitsHelp')} />}
                    value={data.totalVisits}
                    detail={periodLabel}
                >
                    {data.byStatus.length > 0 && data.byStatus.map((item, index) => (
                        <GuideCountChip key={`${item.status ?? 'none'}-${index}`} label={item.statusName} count={item.count} />
                    ))}
                </GuideKpiCard>
                <div className="pas-card">
                    <GuideSectionTitle as="h4" title={tr('activity.byMonth')} />
                    {points.length === 0
                        ? <GuideEmptyState title={tr('activity.emptyVisits')} />
                        : <VisitsByMonthChart points={points} seriesName={tr('activity.seriesName')} />}
                </div>
            </>
        );
    };

    const protocols = useMemo(() => {
        const map = new Map<number, { id: number; name: string; patients?: number; active?: number; visits?: number }>();
        (patients.data?.byProtocol ?? []).forEach((item) =>
            map.set(item.protocolId, { id: item.protocolId, name: protocolLabel(item.protocolNumber, item.protocolName), patients: item.patients, active: item.active }));
        (visits.data?.byProtocol ?? []).forEach((item) =>
            map.set(item.protocolId, { ...(map.get(item.protocolId) ?? { id: item.protocolId, name: protocolLabel(item.protocolNumber, item.protocolName) }), visits: item.count }));
        return Array.from(map.values());
    }, [patients.data, visits.data]);
    const protocolSort = useGuideSort(protocols, {
        protocol: (item) => item.name,
        patients: (item) => item.patients,
        active: (item) => item.active,
        visits: (item) => item.visits
    });

    const renderByProtocol = () => {
        if (protocols.length === 0) return null;
        const sort = protocolSort.sortProps;
        return (
            <div className="pas-card">
                <GuideSectionTitle as="h4" title={tr('activity.byProtocol')} />
                <GuideTable ariaLabel={tr('activity.byProtocol')}>
                    <thead>
                        <tr>
                            <GuideTh sort={sort('protocol')}>{tr('activity.columns.protocol')}</GuideTh>
                            <GuideTh align="end" sort={sort('patients')}>{tr('activity.columns.patients')}</GuideTh>
                            <GuideTh align="end" sort={sort('active')}>{tr('activity.columns.active')}</GuideTh>
                            <GuideTh align="end" sort={sort('visits')}>{tr('activity.columns.visits')}</GuideTh>
                        </tr>
                    </thead>
                    <tbody>
                        {protocolSort.rows.map((item) => (
                            <tr key={item.id} className={guideRowClassName()}>
                                <td>{item.name}</td>
                                <td className={cell('end', 'num')}>{item.patients ?? '—'}</td>
                                <td className={cell('end', 'num')}>{item.active ?? '—'}</td>
                                <td className={cell('end', 'num')}>{item.visits ?? '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </GuideTable>
            </div>
        );
    };

    const range = period.from && period.to ? `${fmtDate(period.from)} – ${fmtDate(period.to)}` : undefined;

    return (
        <div className="pas-root" data-testid="practitioner-account-statement">
            {/* ── 2. Acciones y filtros ── */}
            <div className="pas-toolbar">
                <div className="pas-toolbar__filters">
                    <GuideSegmented<PeriodKey>
                        ariaLabel={tr('filters.period')}
                        options={PERIOD_KEYS.map((key) => ({ value: key, label: tr(`filters.periods.${key}`) }))}
                        value={periodKey}
                        onChange={setPeriodKey}
                    />
                    {range && <span className="ichtys-secondary ichtys-num" data-testid="pas-period-range">{range}</span>}
                    {currencies.length > 1 && (
                        <GuideSegmented
                            label={tr('filters.currency')}
                            ariaLabel={tr('filters.currency')}
                            options={[
                                { value: ALL_CURRENCIES, label: tr('filters.allCurrencies') },
                                ...currencies.map((code) => ({ value: code, label: code }))
                            ]}
                            value={activeCurrency ?? ALL_CURRENCIES}
                            onChange={(value) => setCurrency(value === ALL_CURRENCIES ? undefined : value)}
                        />
                    )}
                </div>
                <div className="pas-toolbar__actions">
                    <Button appearance="secondary" icon={downloading ? <Spinner size="extra-tiny" /> : <ArrowDownload20Regular />} disabled={downloading} onClick={download}>
                        {downloading ? tr('actions.downloading') : tr('actions.downloadExcel')}
                    </Button>
                    <Button appearance="primary" icon={<Add20Regular />} onClick={() => openPayment()}>{tr('actions.registerPayment')}</Button>
                </div>
            </div>
            <p className="ichtys-secondary pas-caption">{tr('filters.caption')}</p>

            {excelError && (
                <GuideMessageBar intent="error"><MessageBarBody>{tr('excel.error')}</MessageBarBody></GuideMessageBar>
            )}
            {payError && (
                <GuideMessageBar intent="error"><MessageBarBody>{payError}</MessageBarBody></GuideMessageBar>
            )}

            {/* ── 1. Saldo ── */}
            <section className="pas-block" aria-labelledby="pas-balance-title">
                <GuideSectionTitle id="pas-balance-title" title={tr('balance.title')} description={tr('intro', { name })} />
                {renderBalance()}
            </section>

            {/* ── 3. Por liquidación ── */}
            <section className="pas-block" aria-labelledby="pas-settlements-title">
                <GuideSectionTitle id="pas-settlements-title" title={tr('settlements.title')}
                    help={<HelpButton title={tr('settlements.title')} text={tr('settlements.help')} />}
                    description={tr('settlements.description')} />
                {renderSettlements()}
            </section>

            {/* ── 4. Pagos ── */}
            <section className="pas-block" aria-labelledby="pas-payments-title">
                <GuideSectionTitle id="pas-payments-title" title={tr('payments.title')}
                    help={<HelpButton title={tr('payments.title')} text={tr('payments.help')} />}
                    description={tr('payments.description', { period: periodLabel.toLowerCase() })} />
                {renderPayments()}
            </section>

            {/* ── Facturas del profesional (contrato §12) ── */}
            <PractitionerInvoicesSection
                practitionerId={practitionerId}
                practitionerName={name}
                invoices={props.Invoices}
                settlements={settlements}
                currencies={currencies}
                payments={payments}
                onPaymentInvoicesChanged={loadMoney}
                LoadCreditorInvoices={props.LoadCreditorInvoices}
                UploadCreditorInvoice={props.UploadCreditorInvoice}
                RetryRegisterCreditorInvoice={props.RetryRegisterCreditorInvoice}
                RegisterSettlementInvoice={props.RegisterSettlementInvoice}
                GetSettlementInvoiceCandidates={props.GetSettlementInvoiceCandidates}
                UpdateCreditorInvoice={props.UpdateCreditorInvoice}
                DeleteCreditorInvoice={props.DeleteCreditorInvoice}
                GetPaymentInvoices={props.GetPaymentInvoices}
                SetPaymentInvoices={props.SetPaymentInvoices}
            />

            {/* ── 5. Actividad ── */}
            <section className="pas-block" aria-labelledby="pas-activity-title">
                <GuideSectionTitle id="pas-activity-title" title={tr('activity.title')}
                    help={<HelpButton title={tr('activity.title')} text={tr('activity.help')} />} />
                <div className="pas-activity">
                    <div className="pas-activity__col">{renderPatients()}</div>
                    <div className="pas-activity__col">{renderVisits()}</div>
                </div>
                {renderByProtocol()}
            </section>

            {/* ── 6. Pago ── */}
            <PaymentFormComponent
                FormType={PaymentFormType.payer}
                ReferalId={props.OwnerId}
                FixedAssociatedEntityIdentifier={undefined}
                PrefillDebtsData={prefill}
                ImputeToCreditorId={practitionerId}
                ImputeToCreditorName={name}
                CandidateDebtsData={pendingDebts}
                isOpen={paymentOpen}
                onClose={closePayment}
            />

            {/* ── Facturas y comprobantes de un pago ── */}
            {invoicesPayment && props.GetPaymentInvoices && props.SetPaymentInvoices && (
                <PaymentInvoicesDrawer
                    open
                    onDismiss={closeInvoicesDrawer}
                    paymentId={invoicesPayment.paymentId}
                    suggestedSettlementIds={paymentSettlementIds(invoicesPayment)}
                    creditorId={practitionerId}
                    creditorName={name}
                    invoices={props.Invoices}
                    settlementNameOf={(id) => settlementLabel(id, settlements)}
                    LoadCreditorInvoices={(id) => props.LoadCreditorInvoices?.(id)}
                    GetPaymentInvoices={props.GetPaymentInvoices}
                    SetPaymentInvoices={props.SetPaymentInvoices}
                    onSaved={() => setInvoicesChanged(true)}
                />
            )}
            {receiptsPayment && buildPaymentReceiptTarget(receiptsPayment.paymentId, practitionerId) && (
                <AzureFileUploadDrawer
                    open
                    onDismiss={closeReceiptsDrawer}
                    title={t('creditorInvoices.counters.receiptsTitle', { id: receiptsPayment.paymentId })}
                    target={buildPaymentReceiptTarget(receiptsPayment.paymentId, practitionerId)!}
                    allowedFileTypes={PAYMENT_RECEIPT_ALLOWED_FILE_TYPES}
                    fileListLoader="paymentReceipt"
                />
            )}
        </div>
    );
};

export default connect(
    Connect.mapStateToProps,
    Connect.mapDispatchToProps
)(PractitionerAccountStatementComponent as any);
